import { numberToPrettyString, plural } from '@cian/utils';
import isNumber from 'lodash/isNumber';

import { getCommuteTypeByRegionId } from './geo';
import { NBSP } from '../constants';
import { IRoadInfoSchema, IStationInfoSchema, IVillageCounterSetSchema } from '../types';

export const getSubdomain = (url?: string | null) => {
  const result = url ? /http[s]?:\/\/(.*?)\./.exec(url) : null;

  return result && result[1];
};

export const isArrayWithItems = <T>(value: T) => Array.isArray(value) && value.length > 0;

export const imageSize = (srcWidth: number, srcHeight: number, maxWidth: number, maxHeight: number) => {
  const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);

  return {
    height: srcHeight * ratio,
    width: srcWidth * ratio,
  };
};

export const parseStringConfig = (param: string, delimeter = ','): number[] =>
  param.split(delimeter).map(Number).filter(Number);

export const getHeaderArray = (header: string | undefined, defaultValue: number[]): number[] => {
  if (!header) {
    return defaultValue;
  }
  const result = parseStringConfig(header);

  return result.length > 0 ? result : defaultValue;
};

export const getDistanceHighway = (
  highway: Pick<IRoadInfoSchema, 'distanceCity'>,
  regionId?: number,
  pretext: string = 'до',
) => {
  const distance = highway.distanceCity;
  const cityLine = getCommuteTypeByRegionId(regionId);

  return `${distance} км ${pretext} ${cityLine}`;
};

export const getDistanceRailway = (
  railway: Pick<IStationInfoSchema, 'distanceCity' | 'terminalName' | 'genitiveTerminalName'>,
) => {
  const { distanceCity, terminalName, genitiveTerminalName } = railway;

  if (!distanceCity || (!terminalName && !genitiveTerminalName)) {
    return '';
  }

  return `${distanceCity} км до ${genitiveTerminalName || terminalName}`;
};

export function rangePrice(min?: number, max?: number): string | undefined {
  const num = min || max;

  if (!isNumber(num)) {
    return undefined;
  }

  let range;
  if (min !== max) {
    range = [
      min ? `от${NBSP}${numberToPrettyString(min)}` : '',
      min && max ? ' ' : '',
      max ? `до${NBSP}${numberToPrettyString(max)}` : '',
    ].join('');
  } else {
    range = numberToPrettyString(num);
  }

  return `${range}${NBSP}₽`;
}

export const getYearRange = (yearMin: number, yearMax: number) => {
  return yearMin === yearMax ? yearMin.toString() : `${yearMin}—${yearMax}`;
};

export const formatNumber = (value: number) => numberToPrettyString(+value.toFixed(2));

const AREA_VARIANTS: [string, string, string] = ['сотки', 'соток', 'соток'];

export const getValuePostfix = (value: number, isLandplot?: boolean) =>
  isLandplot ? plural(value, AREA_VARIANTS) : 'м²';

export const getAreaBlock = (min: number, max: number, isLandplot?: boolean) => {
  if (min !== max) {
    const minPostfix = getValuePostfix(min, isLandplot);
    const maxPostfix = getValuePostfix(max, isLandplot);

    return `от ${formatNumber(min)} ${minPostfix} до ${formatNumber(max)} ${maxPostfix}`;
  }
  const minPostfix = isLandplot ? plural(min, ['сотка', 'сотки', 'соток']) : 'м²';

  return `${formatNumber(min)} ${minPostfix}`;
};

export const getCounterSetOnSale = (
  counterSet: Pick<
    IVillageCounterSetSchema,
    'onSaleTownhouse' | 'onSaleLowrisehouse' | 'onSaleLandplot' | 'onSaleCottage' | 'onSaleHouseShare'
  >,
) => {
  const {
    onSaleTownhouse = 0,
    onSaleLowrisehouse = 0,
    onSaleLandplot = 0,
    onSaleCottage = 0,
    onSaleHouseShare = 0,
  } = counterSet;

  return onSaleTownhouse + onSaleLowrisehouse + onSaleLandplot + onSaleCottage + onSaleHouseShare;
};

export function formatPhone(phone: string, cityCodeNotNecessary?: boolean): string {
  let mask = cityCodeNotNecessary ? '$1 ($2) $3-$4-$5' : '$1 $2 $3-$4-$5';
  if (/^7\d{10}$/.test(phone)) {
    mask = `+${mask}`;
  }

  return phone.replace(/(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/, mask);
}

// Отключаем, т.к. линтер выдаёт Expression is always true
// tslint:disable-next-line:strict-type-predicates
export const isClient = (): boolean => typeof window !== 'undefined';

export const truncateText = (text: string | undefined, count: number): string => {
  if (!text) {
    return '';
  }

  return text.length > count ? `${text.slice(0, count)}...` : text;
};

export const getHighway = (roadInfoList: IRoadInfoSchema[] = []) =>
  roadInfoList.reduce((prev, next) => {
    const { distanceCity: prevDistanceCity = 0 } = prev;
    const { distanceCity: nextDistanceCity = 0 } = next;

    return prevDistanceCity <= nextDistanceCity ? prev : next;
  }, roadInfoList[0]);

export const generateHighwayDescription = (
  { name, distanceCity }: Pick<IRoadInfoSchema, 'distanceCity' | 'name'>,
  regionId: number,
) => {
  const nameView = name ? `${truncateText(name, 35)}, ` : '';
  const distance = distanceCity || 0;
  const cityLine = getCommuteTypeByRegionId(regionId);

  return `${nameView}${distance} км до ${cityLine}`;
};

export type NonNullableObject<T> = {
  [K in keyof T]: NonNullable<T[K]>;
};

export const ensureIsNonNullable = <T extends {}>(item: T): item is NonNullableObject<T> =>
  Object.keys(item).every(key => item[key as keyof T] !== null && item[key as keyof T] !== undefined);
